import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardIdentity } from '@/lib/auth/dashboard-context';
import { navItems } from './navItems';
import { dashboardCopy, getDashboardLanguage } from './language';

export default function MobileNav({ onNavigate }: { onNavigate: () => void }) {
  const identity = useDashboardIdentity();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const copy = dashboardCopy[getDashboardLanguage(i18n.resolvedLanguage ?? i18n.language)];
  const available = navItems.filter((item) => identity?.dashboardPages?.includes(item.to));
  const primary = available.length > 5 ? available.slice(0, 4) : available;
  const extra = available.length > 5 ? available.slice(4) : [];
  const currentPath = pathname.replace(/\/$/, '') || '/';
  const extraActive = extra.some((item) => item.to === currentPath);
  const primaryIndex = primary.findIndex((item) => item.to === currentPath);
  const activeIndex = extraActive ? primary.length : primaryIndex;
  const slotCount = primary.length + (extra.length ? 1 : 0);

  if (!available.length) return null;

  return (
    <nav aria-label="Dashboard pages" className="dashboard-mobile-nav absolute bottom-[max(8px,env(safe-area-inset-bottom))] left-1/2 z-30 w-[min(430px,calc(100%-16px))] -translate-x-1/2 lg:hidden">
      {moreOpen && extra.length > 0 && (
        <div id="dashboard-more-pages" className="absolute bottom-full left-0 right-0 mb-2 rounded-2xl border border-background-200 bg-background-50 p-2 shadow-xl">
          <div className="grid grid-cols-4 gap-1">
            {extra.map((item) => {
              const label = copy.nav[navItems.indexOf(item)];
              return (
                <NavLink key={item.to} to={item.to} end={item.end} onClick={() => { onNavigate(); setMoreOpen(false); }} title={label} className={({ isActive }) => `group flex min-h-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl px-1 text-center focus-visible:outline-none ${isActive ? 'mobile-nav-item-active' : 'text-foreground-700 hover:bg-background-100'}`}>
                  {({ isActive }) => <>
                    <i className={`${item.icon.replace(/-line$/, '-fill')} mobile-nav-extra-icon text-xl leading-none transition-transform duration-200 ease-out group-active:scale-95 motion-reduce:transform-none motion-reduce:transition-none ${isActive ? '-translate-y-0.5 scale-110' : ''}`} aria-hidden="true" />
                    <span className="w-full truncate text-[10px] font-semibold">{label}</span>
                  </>}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      <div className="mobile-nav-bar relative grid items-stretch rounded-2xl border p-1.5" style={{ gridTemplateColumns: `repeat(${slotCount}, minmax(0, 1fr))` }}>
        {activeIndex >= 0 && <span aria-hidden="true" className="mobile-nav-active-indicator pointer-events-none absolute inset-y-[7px] left-[7px] rounded-xl" style={{ width: `calc(${100 / slotCount}% - ${14 / slotCount}px)`, transform: `translateX(${activeIndex * 100}%)` }} />}
        {primary.map((item) => {
          const label = item.label === 'Overview' ? copy.home : copy.nav[navItems.indexOf(item)];
          return (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={() => { onNavigate(); setMoreOpen(false); }} title={label} className={({ isActive }) => `group relative z-10 flex min-h-[60px] min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl px-1 text-center focus-visible:outline-none ${isActive ? 'mobile-nav-item-active' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'}`}>
              {({ isActive }) => <>
                <span className={`mobile-nav-icon flex size-7 items-center justify-center rounded-lg transition-transform duration-200 ease-out group-active:scale-95 motion-reduce:transform-none motion-reduce:transition-none ${isActive ? '-translate-y-0.5 scale-110 mobile-nav-icon-active' : ''}`}>
                  <i className={`${item.icon.replace(/-line$/, '-fill')} text-xl leading-none`} aria-hidden="true" />
                </span>
                {isActive && <span className="mobile-nav-active-label w-full truncate text-[11px] font-bold leading-tight">{label}</span>}
              </>}
            </NavLink>
          );
        })}
        {extra.length > 0 && (
          <button type="button" aria-label={copy.more} aria-expanded={moreOpen} aria-controls="dashboard-more-pages" onClick={() => setMoreOpen((value) => !value)} className={`group relative z-10 flex min-h-[60px] min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl px-1 text-center focus-visible:outline-none ${moreOpen || extraActive ? 'mobile-nav-item-active' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'}`}>
            <span className={`mobile-nav-icon flex size-7 items-center justify-center rounded-lg transition-transform duration-200 ease-out group-active:scale-95 motion-reduce:transform-none motion-reduce:transition-none ${moreOpen || extraActive ? '-translate-y-0.5 scale-110 mobile-nav-icon-active' : ''}`}>
              <i className="ri-apps-2-fill text-xl leading-none" aria-hidden="true" />
            </span>
            {(moreOpen || extraActive) && <span className="mobile-nav-active-label w-full truncate text-[11px] font-bold leading-tight">{copy.more}</span>}
          </button>
        )}
      </div>
    </nav>
  );
}
